import { useState } from "react";
import { Copy, CheckCircle2, ExternalLink, Users, ThumbsUp, ThumbsDown, Sparkles, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Link } from "react-router-dom";

function ExpiryBadge({ expiresAt, couponId }) {
  if (!expiresAt) return null;
  try {
    const exp = new Date(expiresAt);
    const now = new Date();
    const diffMs = exp - now;
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (days < 0 || days > 14) return null;
    const label = days <= 0 ? "Expires today" : days === 1 ? "Expires tomorrow" : `Expires in ${days} days`;
    return (
      <span className="flex items-center gap-1 text-xs font-semibold text-amber-700"
            data-testid={`expiry-badge-${couponId}`}>
        <Clock className="w-3.5 h-3.5" /> {label}
      </span>
    );
  } catch {
    return null;
  }
}

export default function CouponCard({ coupon, showStore = true }) {
  const [revealed, setRevealed] = useState(false);
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [votes, setVotes] = useState({
    up: coupon.upvotes || 0,
    down: coupon.downvotes || 0,
    rate: coupon.success_rate,
  });
  const [voted, setVoted] = useState(() => {
    return typeof localStorage !== "undefined" && localStorage.getItem(`vote_${coupon.id}`);
  });

  const goUrl = `${API}/go/coupon/${coupon.id}`;

  const reveal = async () => {
    try {
      const { data } = await api.post(`/coupons/${coupon.id}/reveal`);
      setCode(data.code || "");
      setRevealed(true);
      // Open store in new tab via tracked redirect
      window.open(goUrl, "_blank", "noopener,noreferrer");
      if (data.code) {
        navigator.clipboard.writeText(data.code).catch(() => {});
        setCopied(true);
        toast.success("Code copied — store opened in new tab");
        setTimeout(() => setCopied(false), 2500);
      } else {
        toast.success("Deal opened in new tab");
      }
    } catch {
      toast.error("Could not reveal code");
    }
  };

  const copy = () => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Copied again");
    setTimeout(() => setCopied(false), 2000);
  };

  const vote = async (dir) => {
    if (voted) return toast("You already voted on this coupon");
    try {
      const { data } = await api.post(`/coupons/${coupon.id}/vote`, { vote: dir });
      setVotes({ up: data.upvotes, down: data.downvotes, rate: data.success_rate });
      localStorage.setItem(`vote_${coupon.id}`, dir);
      setVoted(dir);
      toast.success(dir === "up" ? "Thanks — marked as working!" : "Thanks — flagged as broken");
    } catch {
      toast.error("Vote failed");
    }
  };

  return (
    <article
      className="group border border-slate-200 bg-white hover-lift rounded-sm flex flex-col md:flex-row items-stretch relative"
      data-testid={`coupon-card-${coupon.id}`}
    >
      {coupon.is_new && (
        <span
          className="absolute -top-2 -right-2 z-10 bg-amber-400 text-slate-950 text-[10px] font-bold tracking-wider uppercase px-2 py-1 rounded-sm flex items-center gap-1 shadow-sm"
          data-testid={`new-badge-${coupon.id}`}
        >
          <Sparkles className="w-3 h-3" /> New
        </span>
      )}

      <div className="md:w-32 shrink-0 border-b md:border-b-0 md:border-r border-slate-200 bg-slate-50 flex flex-col items-center justify-center py-6 md:py-4 px-4">
        <p className="font-display text-2xl leading-none text-slate-950 text-center tracking-tighter">
          {coupon.discount_label || "DEAL"}
        </p>
        <span className="overline mt-2 text-slate-500">
          {coupon.deal_type === "code" ? "Code" : "Deal"}
        </span>
      </div>

      <div className="flex-1 p-6 flex flex-col justify-center">
        {showStore && (
          <Link
            to={`/store/${coupon.store_slug}`}
            className="overline text-slate-500 hover:text-emerald-600 mb-2"
            data-testid={`coupon-store-link-${coupon.id}`}
          >
            {coupon.store_name}
          </Link>
        )}
        <h3 className="font-display text-lg leading-tight tracking-tight text-slate-950 mb-2">
          {coupon.title}
        </h3>
        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
          {coupon.verified && (
            <span className="flex items-center gap-1 text-emerald-600 font-medium">
              <CheckCircle2 className="w-3.5 h-3.5" /> Verified
            </span>
          )}
          <span className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" /> {coupon.uses.toLocaleString()} used
          </span>
          <span data-testid={`success-rate-${coupon.id}`}>{votes.rate}% success</span>
          <ExpiryBadge expiresAt={coupon.expires_at} couponId={coupon.id} />

          <div className="flex items-center gap-1 ml-auto md:ml-2">
            <button
              onClick={() => vote("up")}
              disabled={!!voted}
              data-testid={`vote-up-${coupon.id}`}
              title="It worked"
              className={`p-1.5 rounded-sm transition-colors ${
                voted === "up"
                  ? "bg-emerald-100 text-emerald-700"
                  : "hover:bg-emerald-50 hover:text-emerald-600 text-slate-400 disabled:opacity-50"
              }`}
            >
              <ThumbsUp className="w-3.5 h-3.5" />
            </button>
            <span className="text-xs font-medium text-slate-600 tabular-nums w-6 text-center">{votes.up}</span>
            <button
              onClick={() => vote("down")}
              disabled={!!voted}
              data-testid={`vote-down-${coupon.id}`}
              title="It's broken"
              className={`p-1.5 rounded-sm transition-colors ${
                voted === "down"
                  ? "bg-red-100 text-red-700"
                  : "hover:bg-red-50 hover:text-red-600 text-slate-400 disabled:opacity-50"
              }`}
            >
              <ThumbsDown className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      <div className="md:w-56 shrink-0 p-4 flex items-center justify-center border-t md:border-t-0 md:border-l border-slate-200">
        {!revealed ? (
          <button
            onClick={reveal}
            data-testid={`get-code-btn-${coupon.id}`}
            className="w-full py-3 px-5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm tracking-wide rounded-sm transition-all duration-200 group-hover:shadow-md flex items-center justify-center gap-2"
          >
            {coupon.deal_type === "code" ? "GET CODE" : "GET DEAL"}
            <ExternalLink className="w-3.5 h-3.5" strokeWidth={3} />
          </button>
        ) : coupon.deal_type === "code" && code ? (
          <button
            onClick={copy}
            data-testid={`copy-code-btn-${coupon.id}`}
            className="w-full py-3 px-4 border-2 border-dashed border-slate-950 bg-white hover:bg-slate-950 hover:text-emerald-400 font-mono font-bold text-sm tracking-widest rounded-sm transition-colors flex items-center justify-center gap-2"
          >
            {code}
            {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        ) : (
          <div className="w-full py-3 px-4 border-2 border-dashed border-emerald-500 text-emerald-700 font-bold text-sm text-center rounded-sm">
            DEAL ACTIVATED
          </div>
        )}
      </div>
    </article>
  );
}
